import React, { useEffect, useState } from "react";
import { Loader2, Star, Check, Trash2, EyeOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function ReviewsPanel() {
  const [rows, setRows] = useState(null);

  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    await api.put(`/admin/reviews/${r.id}?status=${status}`);
    toast.success(status === "approved" ? `Review from ${r.first_name} is now live` : "Review unpublished");
    load();
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete the review from ${r.first_name}?`)) return;
    await api.delete(`/admin/reviews/${r.id}`);
    toast.success("Review deleted");
    load();
  };

  if (!rows) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" data-testid="reviews-panel-loading" />;
  if (rows.length === 0) return <p className="text-sm text-slate-500" data-testid="reviews-panel-empty">No reviews yet. Families who have sent a booking enquiry can leave one on a tutor's profile, and it will appear here for your approval.</p>;

  const pending = rows.filter((r) => r.status !== "approved").length;

  return (
    <div className="space-y-3" data-testid="reviews-panel">
      {pending > 0 && <p className="text-sm font-semibold text-amber-700" data-testid="reviews-pending-count">{pending} review{pending === 1 ? "" : "s"} waiting for your approval</p>}
      {rows.map((r) => (
        <Card key={r.id} className="border-slate-200 p-5" data-testid={`admin-review-${r.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>
                <Badge variant="secondary" className="text-[10px]">{r.tutor_name}</Badge>
                {r.status === "approved"
                  ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Live</Badge>
                  : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Pending</Badge>}
              </div>
              <p className="text-sm text-slate-700 mt-2">“{r.comment}”</p>
              <p className="text-sm text-slate-900 font-semibold mt-2">
                {r.first_name}
                <span className="font-normal text-slate-500">{r.year_level ? ` · ${r.year_level}` : ""} · {r.email} · {fmt(r.created_at)}</span>
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {r.status === "approved" ? (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(r, "pending")} data-testid={`review-unpublish-${r.id}`}><EyeOff className="w-4 h-4" /> Unpublish</Button>
              ) : (
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(r, "approved")} data-testid={`review-approve-${r.id}`}><Check className="w-4 h-4" /> Approve</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => remove(r)} data-testid={`review-delete-${r.id}`}><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
